import type { ReactNode } from 'react'
import { format } from 'date-fns'
import { Check, Minus } from 'lucide-react'
import { Drawer } from '../../components/ui/Drawer'
import type { ContentEntity } from '../../lib/api/content'
import { cn } from '../../lib/cn'

type El = Record<string, unknown>

const isYes = (v: unknown) => v === true || v === 'true' || v === 1 || v === '1'
function txt(v: unknown): ReactNode {
  return v == null || String(v).trim() === '' ? <span className="text-faint">—</span> : String(v)
}
// Yep dates are ISO strings ("2026-06-05T12:24:39Z"); some models send epoch numbers — handle both.
function entityDateTime(v: unknown): string {
  if (v == null || v === '') return '—'
  if (typeof v === 'number') {
    const ms = v < 1e12 ? v * 1000 : v
    try {
      return format(new Date(ms), 'dd/MM/yyyy HH:mm:ss')
    } catch {
      return '—'
    }
  }
  // render the ISO wall-clock as-is (matches what the Yep panel shows, no timezone shift)
  const m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})/)
  if (m) return `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}:${m[6]}`
  const d = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/)
  return d ? `${d[3]}/${d[2]}/${d[1]}` : String(v)
}
const nonEmpty = (v: unknown) => v != null && String(v).trim() !== ''

function YesNo({ v }: { v: unknown }) {
  const yes = isYes(v)
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-[2px] text-[11.5px] font-semibold',
        yes ? 'bg-ok-soft text-ok' : 'bg-paper text-faint ring-1 ring-line',
      )}
    >
      {yes ? <Check className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
      {yes ? 'Sì' : 'No'}
    </span>
  )
}
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-line-soft py-[9px] last:border-0">
      <span className="shrink-0 text-[12.5px] text-muted">{label}</span>
      <span className="max-w-[64%] text-right text-[13px] font-medium text-coal [overflow-wrap:anywhere]">{value}</span>
    </div>
  )
}
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="mb-4">
      <h3 className="mb-1.5 px-1 text-[11px] font-bold uppercase tracking-[1.4px] text-muted">{title}</h3>
      <div className="cv-panel px-4">{children}</div>
    </section>
  )
}

export function MemberDrawer({ row, onClose }: { row: ContentEntity | null; onClose: () => void }) {
  const el = (row?.elements ?? {}) as El
  const via = el.facebookID ? 'Facebook' : el.googleID ? 'Google' : 'Email'
  const fullName = `${String(el.name ?? '')} ${String(el.lastName ?? '')}`.trim() || String(el.username ?? '—')

  // billing address sub-fields may arrive nested under `billingAddress` or flattened to top level
  const addr = (el.billingAddress ?? {}) as El
  const af = (k: string): unknown => {
    const v = addr[k]
    return nonEmpty(v) ? v : el[k]
  }

  return (
    <Drawer
      open={Boolean(row)}
      onClose={onClose}
      tone="brand"
      title={<span className="text-[15px] font-bold text-white">{fullName}</span>}
      subtitle={`${String(el.contactEmail ?? '—')} · registrato via ${via}`}
    >
      <Section title="Account">
        <Row label="Nome utente" value={<span className="font-mono text-[12.5px]">{txt(el.username)}</span>} />
        <Row label="Registrato via" value={via} />
        <Row label="Stato" value={<YesNo v={el.enabled} />} />
      </Section>

      <Section title="Anagrafica">
        <Row label="Nome" value={txt(el.name)} />
        <Row label="Cognome" value={txt(el.lastName)} />
        <Row label="Email" value={txt(el.contactEmail)} />
        <Row label="Telefono" value={txt(el.phoneNumber)} />
        <Row label="Nazionalità" value={txt(el.country)} />
        <Row
          label="Targa"
          value={
            el.licencePlate ? (
              <span className="rounded-md border border-line bg-paper px-2 py-0.5 font-mono text-[12px] font-semibold tracking-wider text-coal">
                {String(el.licencePlate)}
              </span>
            ) : (
              <span className="text-faint">—</span>
            )
          }
        />
      </Section>

      <Section title="Fatturazione">
        <Row label="Ragione sociale / Cognome nome" value={txt(el.businessName)} />
        <Row label="Codice fiscale / Partita Iva" value={txt(el.businessVatId)} />
        <Row label="Codice SDI" value={txt(el.businessSDI)} />
        <Row label="PEC" value={txt(el.businessPec)} />
        <Row label="Via / Piazza" value={txt(af('businessAddress'))} />
        <Row label="Numero" value={txt(af('businessHouseNumber'))} />
        <Row label="Città" value={txt(af('businessCity'))} />
        <Row label="Codice postale" value={txt(af('businessZipCode'))} />
        <Row label="Provincia" value={txt(af('businessRegionProvince'))} />
        <Row label="Nazione" value={txt(af('businessCountry'))} />
      </Section>

      <Section title="Consensi privacy">
        <Row label="Trattamento dei dati personali" value={<YesNo v={el.privacy} />} />
        <Row label="Marketing e promozioni" value={<YesNo v={el.commercialPrivacy} />} />
      </Section>

      <Section title="Informazioni">
        <Row label="Creato" value={entityDateTime(row?.creationDate)} />
        <Row label="Ultima modifica" value={entityDateTime(row?.lastUpdate)} />
      </Section>
    </Drawer>
  )
}
